import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  IoCheckmarkCircle,
  IoClose,
  IoCloseCircle,
  IoDownload,
  IoEye,
} from "react-icons/io5";
import { FiFilter, FiRotateCcw, FiSearch } from "react-icons/fi";
import { useOrderPayments } from "../useHook";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";
import Loader from "../../../Utils/loader";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";

function looksLikeEmail(value) {
  const v = value.trim();
  if (!v.includes("@")) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function formatPaymentType(type) {
  const method = String(type || "").toLowerCase();
  if (method === "pay now" || method.includes("card")) {
    return "Credit Debit Card Payment";
  }
  if (method.includes("transfer") || method.includes("bank")) {
    return "Bank Transfer Payment";
  }
  if (method.includes("cash on pickup") || method === "cop") {
    return "Cash On Pickup";
  }
  return type || "N/A";
}

function formatMoney(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return `Rs. ${amount.toLocaleString("en-PK", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return `${date.toLocaleDateString("en-GB").replace(/\//g, "-")} ${date.toLocaleTimeString(
    "en-US",
    { hour: "2-digit", minute: "2-digit", hour12: true },
  )}`;
}

function getCustomerName(payment) {
  if (payment?.customerName) return payment.customerName;
  const user = payment?.user || payment?.order?.user;
  if (!user) return "—";
  return `${user.firstName || ""} ${user.lastName || ""}`.trim() || "—";
}

function getCustomerEmail(payment) {
  return (
    payment?.customerEmail ||
    payment?.user?.email ||
    payment?.order?.user?.email ||
    "—"
  );
}

function getCustomerPhone(payment) {
  return (
    payment?.customerPhone ||
    payment?.user?.contactNo ||
    payment?.order?.user?.contactNo ||
    payment?.billingAddress?.contactNo ||
    payment?.order?.userBillingAddress?.contactNo ||
    ""
  );
}

function getProducts(payment) {
  if (Array.isArray(payment?.products) && payment.products.length) {
    return payment.products;
  }
  const info = payment?.order?.productInfo;
  return Array.isArray(info) ? info : [];
}

function getTotalItems(payment) {
  if (payment?.totalItems != null) return payment.totalItems;
  return getProducts(payment).reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0,
  );
}

function getAmount(payment) {
  return payment?.totalAmount ?? payment?.order?.totalAmount;
}

function getDeliveryCharges(payment) {
  return payment?.deliveryCharges ?? payment?.order?.deliveryCharges;
}

function getBillingAddress(payment) {
  return payment?.billingAddress || payment?.order?.userBillingAddress || null;
}

function getDeliveryLabel(payment) {
  const delivery =
    payment?.deliveryType || payment?.order?.deliveryType || null;
  if (!delivery) return "—";
  return delivery.deliveryType || delivery.type || "—";
}

function statusBadgeClass(status) {
  if (status === "approved") return "bg-green-100 text-green-700";
  if (status === "rejected") return "bg-red-100 text-red-700";
  return "bg-yellow-100 text-yellow-800";
}

function ReceiptThumb({ src, alt, onClick }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className="w-16 h-16 rounded border border-dashed border-gray-300 bg-gray-50 text-[11px] text-gray-400 flex items-center justify-center text-center px-1">
        No receipt
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-16 h-16 rounded border border-gray-200 overflow-hidden bg-white hover:ring-2 hover:ring-green-500"
      title="View receipt"
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        onError={() => setFailed(true)}
      />
    </button>
  );
}

function VerifyPayments() {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canAccept = hasPermission(SystemPermissions.Orders.ACCEPT);
  const canReject = hasPermission(SystemPermissions.Orders.REJECT);
  const canDownloadSlip = hasPermission(SystemPermissions.Orders.DOWNLOAD_SLIP);
  const canViewPending = hasPermission(
    SystemPermissions.Orders.VIEW_PENDING_PAYMENTS,
  );
  const canViewApproved = hasPermission(
    SystemPermissions.Orders.VIEW_APPROVED_PAYMENTS,
  );
  const canViewRejected = hasPermission(
    SystemPermissions.Orders.VIEW_REJECTED_PAYMENTS,
  );
  const canViewInvoice = hasPermission(SystemPermissions.Orders.VIEW);

  const { getPayment, verifyPayment } = useOrderPayments();
  const [rows, setRows] = useState([]);
  const [searchName, setSearchName] = useState("");
  const [searchOrderId, setSearchOrderId] = useState("");
  const [searchStatus, setSearchStatus] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPayment, setCurrentPayment] = useState(null);
  const [currentAction, setCurrentAction] = useState("");
  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    perPage: 9,
  });
  const [offset, setOffset] = useState(0);
  const [limit] = useState(9);

  useEffect(() => {
    fetchPaymentData();
  }, [offset]);

  const buildQueryParams = (pageOffset, filters = {}) => {
    const params = {
      offset: pageOffset,
      limit,
    };
    if (canViewPending) params.pending = true;
    if (canViewApproved) params.approved = true;
    if (canViewRejected) params.rejected = true;

    const nameOrEmailQuery = (filters.name ?? searchName).trim();
    if (nameOrEmailQuery) {
      if (looksLikeEmail(nameOrEmailQuery)) {
        params.email = nameOrEmailQuery;
      } else {
        params.name = nameOrEmailQuery;
      }
    }
    const orderIdQuery = (filters.orderId ?? searchOrderId).trim();
    if (orderIdQuery) params.orderId = orderIdQuery;
    const statusQuery = (filters.status ?? searchStatus).trim();
    if (statusQuery) params.status = statusQuery;
    return params;
  };

  const fetchPaymentData = async (pageOffset = offset, filters) => {
    const params = buildQueryParams(pageOffset, filters);
    if (!params.pending && !params.approved && !params.rejected) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const response = await getPayment(params);
      const payload = response?.data || {};
      const list = Array.isArray(payload.payments)
        ? payload.payments
        : [
            ...(payload.pendingPaymentScreenshots || []),
            ...(payload.approvedPaymentScreenshots || []),
            ...(payload.rejectedPaymentScreenshots || []),
          ];
      setRows(list);
      if (payload.pagination) {
        setPagination({
          totalRecords: payload.pagination.totalRecords,
          totalPages: payload.pagination.totalPages,
          currentPage: payload.pagination.currentPage,
          perPage: payload.pagination.perPage,
        });
      }
    } catch (error) {
      console.error("Error fetching payment data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (offset !== 0) {
      setOffset(0);
      return;
    }
    await fetchPaymentData(0);
  };

  const handleReset = async () => {
    setSearchName("");
    setSearchOrderId("");
    setSearchStatus("");
    setOffset(0);
    try {
      setIsLoading(true);
      await fetchPaymentData(0, { name: "", orderId: "", status: "" });
    } catch (error) {
      console.error("Error fetching payment data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
  };

  const openConfirm = (payment, action) => {
    setCurrentPayment(payment);
    setCurrentAction(action);
    setShowModal(true);
  };

  const handleDownload = (image, orderId) => {
    if (!image) {
      Swal.fire({
        icon: "info",
        title: "No receipt",
        text: "This payment does not have a screenshot uploaded.",
      });
      return;
    }
    const link = document.createElement("a");
    link.href = image;
    link.download = `payment-receipt-${orderId || "slip"}.jpg`;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const confirmAction = async () => {
    if (!currentAction || !currentPayment?.orderId) {
      setShowModal(false);
      return;
    }
    const status = currentAction === "accept" ? "approved" : "rejected";
    const orderId = currentPayment.orderId;
    try {
      setIsSubmitting(true);
      const response = await verifyPayment(orderId, status);
      if (response?.status === 200 || response?.data?.success) {
        setSelectedPayment(null);
        Swal.fire({
          icon: "success",
          title: `Payment ${
            currentAction === "accept" ? "Approved" : "Rejected"
          }`,
          text: `Payment for Order ${orderId} has been ${
            currentAction === "accept" ? "approved" : "rejected"
          }.`,
        });
        await fetchPaymentData(offset);
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed to Update Status",
          text: "An error occurred while updating the payment status. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error while updating payment status", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An unexpected error occurred while updating the payment status.",
      });
    } finally {
      setIsSubmitting(false);
      setShowModal(false);
      setCurrentPayment(null);
      setCurrentAction("");
    }
  };

  const cancelAction = () => {
    if (isSubmitting) return;
    setShowModal(false);
    setCurrentPayment(null);
    setCurrentAction("");
  };

  const renderProductSummary = (payment) => {
    const products = getProducts(payment);
    const totalItems = getTotalItems(payment);
    if (!products.length) {
      return <span className="text-gray-400">{totalItems || 0} items</span>;
    }
    const names = products
      .map((item) => item.productName)
      .filter(Boolean)
      .slice(0, 2);
    const extra = products.length > 2 ? ` +${products.length - 2} more` : "";
    return (
      <div>
        <div className="font-medium text-gray-800">{totalItems} item(s)</div>
        <div className="text-xs text-gray-500 line-clamp-2">
          {names.join(", ")}
          {extra}
        </div>
      </div>
    );
  };

  const renderTable = (data) => (
    <div className="bg-white w-full rounded-lg pagination-shadow">
      <div className="overflow-x-auto">
        <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
          <thead>
            <tr className="brand-gradient text-white">
              <th className="table-th">Sr. No</th>
              <th className="table-th">Receipt</th>
              <th className="table-th">Order ID</th>
              <th className="table-th">Customer</th>
              <th className="table-th">Items</th>
              <th className="table-th">Amount</th>
              <th className="table-th">Payment Type</th>
              <th className="table-th">Submitted</th>
              <th className="table-th">Status</th>
              <th className="table-th">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={10}
                  className="table-td text-center text-gray-500 italic"
                >
                  No records found
                </td>
              </tr>
            ) : (
              data.map((order, index) => {
                const isPending = order.paymentStatus === "pending";
                return (
                <tr
                  key={order.id || `${order.orderId}-${index}`}
                  className={`${
                    index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                  } hover:bg-gray-200 transition-colors duration-300`}
                >
                  <td className="table-td">{offset + index + 1}</td>
                  <td className="table-td">
                    <ReceiptThumb
                      src={order.screenShot}
                      alt={`Receipt for ${order.orderId}`}
                      onClick={() =>
                        order.screenShot && setPreviewUrl(order.screenShot)
                      }
                    />
                  </td>
                  <td className="table-td font-semibold text-gray-800">
                    {order.orderId || "—"}
                  </td>
                  <td className="table-td">
                    <div className="font-medium text-gray-900">
                      {getCustomerName(order)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {getCustomerEmail(order)}
                    </div>
                    {getCustomerPhone(order) ? (
                      <div className="text-xs text-gray-400">
                        {getCustomerPhone(order)}
                      </div>
                    ) : null}
                  </td>
                  <td className="table-td">{renderProductSummary(order)}</td>
                  <td className="table-td font-semibold text-gray-800">
                    {formatMoney(getAmount(order))}
                  </td>
                  <td className="table-td">
                    {formatPaymentType(order.paymentType)}
                  </td>
                  <td className="table-td whitespace-nowrap">
                    {formatDateTime(order.createdAt)}
                  </td>
                  <td className="table-td">
                    <span
                      className={`capitalize px-2 py-1 rounded text-xs font-semibold ${statusBadgeClass(
                        order.paymentStatus || (isPending ? "pending" : ""),
                      )}`}
                    >
                      {order.paymentStatus || (isPending ? "pending" : "—")}
                    </span>
                  </td>
                  <td className="table-td">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedPayment(order)}
                        className="btn btn-view justify-center"
                      >
                        <IoEye className="text-white" /> Review
                      </button>
                      {isPending && canAccept && (
                        <button
                          type="button"
                          onClick={() => openConfirm(order, "accept")}
                          className="btn btn-primary justify-center"
                        >
                          <IoCheckmarkCircle /> Accept
                        </button>
                      )}
                      {isPending && canReject && (
                        <button
                          type="button"
                          onClick={() => openConfirm(order, "reject")}
                          className="btn btn-delete text-white justify-center"
                        >
                          <IoCloseCircle /> Reject
                        </button>
                      )}
                      {canDownloadSlip && order.screenShot && (
                        <button
                          type="button"
                          onClick={() =>
                            handleDownload(order.screenShot, order.orderId)
                          }
                          className="btn btn-action justify-center"
                        >
                          <IoDownload /> Slip
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const reviewPayment = selectedPayment;
  const reviewProducts = reviewPayment ? getProducts(reviewPayment) : [];
  const reviewBilling = reviewPayment ? getBillingAddress(reviewPayment) : null;
  const reviewAmount = reviewPayment ? Number(getAmount(reviewPayment)) || 0 : 0;
  const reviewDelivery = reviewPayment
    ? Number(getDeliveryCharges(reviewPayment)) || 0
    : 0;
  const confirmPayment = currentPayment;

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Verify Payments</h2>
          <p className="text-sm text-gray-500 mt-1">
            Review the customer, order items, amount, and payment receipt before
            approving or rejecting.
          </p>
        </div>
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            placeholder="Search by name or email"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            className="search-input"
          />
          <FiSearch className="search-icon" />
        </div>
        <div className="search-wrapper">
          <input
            type="text"
            placeholder="Search by Order ID"
            value={searchOrderId}
            onChange={(e) => setSearchOrderId(e.target.value)}
            className="search-input"
          />
          <FiSearch className="search-icon" />
        </div>
        <select
          value={searchStatus}
          onChange={(e) => setSearchStatus(e.target.value)}
          className="status-select"
        >
          <option value="">Status</option>
          {canViewApproved && <option value="approved">Approved</option>}
          {canViewPending && <option value="pending">Pending</option>}
          {canViewRejected && <option value="rejected">Rejected</option>}
        </select>

        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      {isLoading ? (
        <Loader message="Loading payments..." fullScreen={false} />
      ) : (
        <>
          {renderTable(rows)}
          {pagination.totalPages > 1 && (
            <div className="mt-6">
              <GlobalPagination
                offset={offset}
                limit={limit}
                total={pagination.totalRecords}
                onPageChange={handlePageChange}
                className="mt-4"
              />
            </div>
          )}
        </>
      )}

      {reviewPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3 border-b px-5 py-4 sticky top-0 bg-white z-10">
              <div>
                <h3 className="text-xl font-bold">Review payment</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {reviewPayment.orderId} ·{" "}
                  {formatPaymentType(reviewPayment.paymentType)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedPayment(null)}
                className="text-gray-500 hover:text-gray-800 p-1"
                aria-label="Close"
              >
                <IoClose size={24} />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-5">
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  Payment receipt
                </p>
                {reviewPayment.screenShot ? (
                  <button
                    type="button"
                    onClick={() => setPreviewUrl(reviewPayment.screenShot)}
                    className="block w-full"
                  >
                    <img
                      src={reviewPayment.screenShot}
                      alt={`Receipt for ${reviewPayment.orderId}`}
                      className="w-full max-h-[520px] object-contain rounded border bg-gray-50"
                    />
                  </button>
                ) : (
                  <div className="h-64 rounded border border-dashed flex items-center justify-center text-gray-400">
                    No receipt uploaded
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="rounded-lg border p-4">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="font-semibold">Customer</h4>
                    <span
                      className={`capitalize px-2 py-1 rounded text-xs font-semibold ${statusBadgeClass(
                        reviewPayment.paymentStatus,
                      )}`}
                    >
                      {reviewPayment.paymentStatus || "—"}
                    </span>
                  </div>
                  <p className="mt-2 text-gray-900 font-medium">
                    {getCustomerName(reviewPayment)}
                  </p>
                  <p className="text-sm text-gray-600">
                    {getCustomerEmail(reviewPayment)}
                  </p>
                  {getCustomerPhone(reviewPayment) ? (
                    <p className="text-sm text-gray-600">
                      {getCustomerPhone(reviewPayment)}
                    </p>
                  ) : null}
                  {reviewBilling ? (
                    <p className="text-sm text-gray-600 mt-2">
                      {reviewBilling.selfPickup &&
                      (reviewBilling.pickupPoint?.address ||
                        reviewPayment?.order?.locationInfo?.pickupPoint
                          ?.address)
                        ? [
                            "Pickup",
                            reviewBilling.pickupPoint?.name ||
                              reviewPayment?.order?.locationInfo?.pickupPoint
                                ?.name,
                            reviewBilling.pickupPoint?.address ||
                              reviewPayment?.order?.locationInfo?.pickupPoint
                                ?.address,
                            reviewBilling.city,
                          ]
                            .filter(Boolean)
                            .join(" · ")
                        : [
                            reviewBilling.streetAddress,
                            reviewBilling.city,
                            reviewBilling.postalCode,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                    </p>
                  ) : null}
                </div>

                <div className="rounded-lg border p-4">
                  <h4 className="font-semibold mb-2">Order details</h4>
                  <div className="text-sm text-gray-700 space-y-1">
                    <p>
                      <span className="font-medium">Submitted:</span>{" "}
                      {formatDateTime(reviewPayment.createdAt)}
                    </p>
                    <p>
                      <span className="font-medium">Delivery:</span>{" "}
                      {getDeliveryLabel(reviewPayment)}
                    </p>
                    <p>
                      <span className="font-medium">Items:</span>{" "}
                      {getTotalItems(reviewPayment)}
                    </p>
                  </div>
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-gray-50 text-left">
                          <th className="p-2 border">Product</th>
                          <th className="p-2 border">Qty</th>
                          <th className="p-2 border">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reviewProducts.length ? (
                          reviewProducts.map((item, index) => (
                            <tr key={`${item.productName}-${index}`}>
                              <td className="p-2 border">
                                {item.productName || "Item"}
                              </td>
                              <td className="p-2 border">
                                {item.quantity || 0}
                              </td>
                              <td className="p-2 border">
                                {formatMoney(
                                  item.itemDiscountedTotal ??
                                    item.discountedPrice ??
                                    item.originalPrice,
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={3}
                              className="p-2 border text-gray-400 text-center"
                            >
                              No product details
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 text-sm space-y-1">
                    <div className="flex justify-between">
                      <span>Subtotal</span>
                      <span>{formatMoney(reviewAmount)}</span>
                    </div>
                    {reviewDelivery > 0 && (
                      <div className="flex justify-between">
                        <span>Delivery</span>
                        <span>{formatMoney(reviewDelivery)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-green-700 pt-1 border-t">
                      <span>Grand total</span>
                      <span>{formatMoney(reviewAmount + reviewDelivery)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {canViewInvoice && reviewPayment.orderId && (
                    <button
                      type="button"
                      className="btn btn-view justify-center"
                      onClick={() =>
                        navigate("/orders/invoice", {
                          state: { orderId: reviewPayment.orderId },
                        })
                      }
                    >
                      <IoEye className="text-white" /> Full invoice
                    </button>
                  )}
                  {canDownloadSlip && reviewPayment.screenShot && (
                    <button
                      type="button"
                      className="btn btn-action justify-center"
                      onClick={() =>
                        handleDownload(
                          reviewPayment.screenShot,
                          reviewPayment.orderId,
                        )
                      }
                    >
                      <IoDownload /> Download slip
                    </button>
                  )}
                  {reviewPayment.paymentStatus === "pending" && canReject && (
                    <button
                      type="button"
                      className="btn btn-delete text-white justify-center"
                      onClick={() => openConfirm(reviewPayment, "reject")}
                    >
                      <IoCloseCircle /> Reject
                    </button>
                  )}
                  {reviewPayment.paymentStatus === "pending" && canAccept && (
                    <button
                      type="button"
                      className="btn btn-primary justify-center"
                      onClick={() => openConfirm(reviewPayment, "accept")}
                    >
                      <IoCheckmarkCircle /> Accept
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && confirmPayment && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
          <div className="bg-white p-6 rounded-lg w-full max-w-lg">
            <h3 className="text-xl font-bold text-center">
              {currentAction === "accept"
                ? "Approve this payment?"
                : "Reject this payment?"}
            </h3>
            <p className="text-sm text-gray-500 text-center mt-1">
              Double-check the receipt and order details before confirming.
            </p>
            <div className="mt-4 flex gap-4">
              {confirmPayment.screenShot ? (
                <img
                  src={confirmPayment.screenShot}
                  alt="Payment receipt"
                  className="w-28 h-28 object-cover rounded border"
                />
              ) : (
                <div className="w-28 h-28 rounded border flex items-center justify-center text-xs text-gray-400">
                  No receipt
                </div>
              )}
              <div className="text-sm text-gray-700 space-y-1">
                <p>
                  <span className="font-semibold">Order:</span>{" "}
                  {confirmPayment.orderId}
                </p>
                <p>
                  <span className="font-semibold">Customer:</span>{" "}
                  {getCustomerName(confirmPayment)}
                </p>
                <p>
                  <span className="font-semibold">Amount:</span>{" "}
                  {formatMoney(getAmount(confirmPayment))}
                </p>
                <p>
                  <span className="font-semibold">Items:</span>{" "}
                  {getTotalItems(confirmPayment)}
                </p>
                <p>
                  <span className="font-semibold">Payment:</span>{" "}
                  {formatPaymentType(confirmPayment.paymentType)}
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-center gap-3">
              <button
                onClick={confirmAction}
                disabled={isSubmitting}
                className={`btn justify-center ${
                  currentAction === "accept" ? "btn-primary" : "btn-delete text-white"
                }`}
              >
                {isSubmitting
                  ? "Saving..."
                  : currentAction === "accept"
                    ? "Yes, approve"
                    : "Yes, reject"}
              </button>
              <button
                onClick={cancelAction}
                disabled={isSubmitting}
                className="btn btn-reset-filter justify-center"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {previewUrl && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setPreviewUrl(null)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 text-white text-3xl"
            onClick={() => setPreviewUrl(null)}
            aria-label="Close preview"
          >
            ×
          </button>
          <img
            src={previewUrl}
            alt="Payment receipt preview"
            className="max-w-full max-h-[90vh] object-contain rounded shadow-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default VerifyPayments;
